import React, { useMemo } from 'react';
import * as THREE from 'three';
import { Vector3 } from 'three';
import { useThree } from '@react-three/fiber';
import { BeamTruss } from '../../types/beamTruss';
import {
  getZoomScale,
  computeBeamLocalAxes,
  generateArcPoints,
  createArrowQuaternion,
  LOAD_VISUAL_CONFIG,
} from '../../utils/loadVisualizationUtils';
import { getLoadVisualScale } from '@/utils/gridScale';

interface TwistedLoadVisualization3DProps {
  beamTruss: BeamTruss;
  startPoint: Vector3;
  endPoint: Vector3;
  visible?: boolean;
  scaleFactor?: number;
}

/**
 * TwistedLoadVisualization3D Component
 * 
 * Renders torsional moment as a 75% circular arrow at beam midpoint.
 * Uses shared utilities for consistent sizing and behavior.
 */
export const TwistedLoadVisualization3D: React.FC<TwistedLoadVisualization3DProps> = ({
  beamTruss,
  startPoint,
  endPoint,
  visible = true,
  scaleFactor = 1,
}) => {
  const { camera } = useThree();
  
  // Get load visual scale from pre-calculated fixed values
  // scaleFactor now represents scaleRange from useUIStore
  const visualScale = getLoadVisualScale(scaleFactor);

  const twistedLoadGeometry = useMemo(() => {
    if (!visible || beamTruss.type !== 'twisted_load') return null;

    // Calculate beam midpoint and direction
    const midpoint = new THREE.Vector3().lerpVectors(startPoint, endPoint, 0.5);
    const { y: perpDir1, z: perpDir2 } = computeBeamLocalAxes(startPoint, endPoint);

    // Get zoom-adaptive scale combined with model scale
    const zoomScale = getZoomScale(camera, midpoint) * visualScale;

    // Calculate arc radius (base config * zoom scale)
    const radius = LOAD_VISUAL_CONFIG.base.circularArcRadius * zoomScale;

    // Generate 75% circular arc (270 degrees)
    const arcAngle = (3 * Math.PI) / 2;
    const isClockwise = beamTruss.torqueDirection === 'clockwise';
    const arcPoints = generateArcPoints(
      midpoint,
      radius,
      perpDir1,
      perpDir2,
      arcAngle,
      isClockwise
    );

    // Calculate arrow cone position at arc end
    const dirMultiplier = isClockwise ? 1 : -1;
    const lastAngle = arcAngle * dirMultiplier;
    const arrowPosition = new THREE.Vector3()
      .copy(midpoint)
      .addScaledVector(perpDir1, Math.cos(lastAngle) * radius)
      .addScaledVector(perpDir2, Math.sin(lastAngle) * radius);

    // Calculate tangent direction for cone orientation
    const tangentAngle = lastAngle + (Math.PI / 2) * dirMultiplier;
    const tangentDir = new THREE.Vector3()
      .addScaledVector(perpDir1, Math.cos(tangentAngle))
      .addScaledVector(perpDir2, Math.sin(tangentAngle))
      .normalize();

    // Arrow cone size from config
    const coneRadius = LOAD_VISUAL_CONFIG.base.arrowHeadWidth * 0.5 * zoomScale;
    const coneHeight = LOAD_VISUAL_CONFIG.base.arrowHeadLength * zoomScale;
    const tubeRadius = LOAD_VISUAL_CONFIG.base.circularArcTubeRadius * zoomScale;

    return {
      arcPoints,
      arrowPosition,
      arrowDirection: tangentDir,
      coneRadius,
      coneHeight,
      tubeRadius,
    };
  }, [beamTruss, startPoint, endPoint, visible, camera, visualScale]);

  if (!visible || !twistedLoadGeometry) return null;

  const { arcPoints, arrowPosition, arrowDirection, coneRadius, coneHeight, tubeRadius } = twistedLoadGeometry;
  const color = LOAD_VISUAL_CONFIG.colors.twisted;
  const quaternion = createArrowQuaternion(arrowDirection);

  return (
    <group>
      {/* Circular arc as tube */}
      <mesh>
        <tubeGeometry
          args={[
            new THREE.CatmullRomCurve3(arcPoints),
            arcPoints.length,
            tubeRadius,
            8,
            false,
          ]}
        />
        <meshBasicMaterial color={color} />
      </mesh>

      {/* Arrow cone */}
      <mesh position={arrowPosition.toArray()} quaternion={quaternion}>
        <coneGeometry args={[coneRadius, coneHeight, 16]} />
        <meshBasicMaterial color={color} />
      </mesh>

      {/* Cone outline */}
      <mesh position={arrowPosition.toArray()} quaternion={quaternion}>
        <coneGeometry args={[coneRadius + 0.01, coneHeight + 0.01, 12]} />
        <meshBasicMaterial color="black" wireframe transparent opacity={0.6} />
      </mesh>
    </group>
  );
};

export default TwistedLoadVisualization3D;
